import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RETURN_STATUS_COLORS, buildReturnStatusMap } from "../../utils/orderStatus";
import { useModal } from "../../context/ModalContext";
import Select from "../../components/Select";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_COLORS = RETURN_STATUS_COLORS;

export default function AdminReturns() {
  const { showAlert, showConfirm, showPrompt } = useModal();
  const { t } = useTranslation();
  const STATUS_MAP = buildReturnStatusMap(t);
  const [returns, setReturns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterStatus) params.append("status", filterStatus);
      const res = await fetch(`${API}/api/returns?${params}`);
      const data = await res.json();
      setReturns(data.returns || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, filterStatus]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  async function updateReturn(id, updates) {
    try {
      await fetch(`${API}/api/returns/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      fetchReturns();
      if (selected?.id === id) setSelected((p) => ({ ...p, ...updates }));
    } catch (e) { showAlert("업데이트 실패"); }
  }

  async function approveRequest(ret) {
    const rtype = ret.request_type || ret.type;
    const msg = rtype === "exchange"
      ? "교환 신청을 승인하시겠습니까?\n수거 단계로 넘어갑니다."
      : "취소/환불 신청을 승인하시겠습니까?\nPayPal에서 결제 전액이 자동 환불됩니다.";
    if (!(await showConfirm(msg))) return;

    try {
      const res = await fetch(`${API}/api/returns/${ret.id}/approve`, {
        method: "POST",
      });
      const data = await res.json();
      if (res.ok) {
        showAlert("승인 처리 완료");
        fetchReturns();
        if (selected?.id === ret.id) setSelected(null);
      } else {
        showAlert(`승인 실패: ${data.error || JSON.stringify(data)}`);
      }
    } catch (e) {
      showAlert("승인 요청 실패: " + e.message);
    }
  }

  async function rejectRequest(ret) {
    const reason = ret._rejectReason;
    if (!reason || !reason.trim()) {
      showAlert("거절 사유를 입력하세요.");
      return;
    }
    if (!(await showConfirm(`거절 사유: "${reason}"\n\n이 사유로 거절하시겠습니까?`))) return;

    try {
      const res = await fetch(`${API}/api/returns/${ret.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reject_reason: reason }),
      });
      const data = await res.json();
      if (res.ok) {
        showAlert("거절 처리 완료");
        fetchReturns();
        if (selected?.id === ret.id) setSelected(null);
      } else {
        showAlert(`거절 실패: ${data.error || JSON.stringify(data)}`);
      }
    } catch (e) {
      showAlert("거절 요청 실패: " + e.message);
    }
  }

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="admin-page">
      <div className="admin-title">취소 / 교환 / 환불 관리</div>

      <div className="ao-filter">
        <Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
          <option value="">전체 상태</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </Select>
        <span className="ao-count">총 {total}건</span>
      </div>

      <div className="ao-table-wrap">
        <table className="ao-table">
          <thead>
            <tr>
              <th>신청일</th>
              <th>유형</th>
              <th>주문번호</th>
              <th>고객명</th>
              <th>사유</th>
              <th>상태</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40 }}>로딩 중...</td></tr>
            ) : returns.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#999" }}>내역이 없습니다.</td></tr>
            ) : returns.map((r) => (
              <tr key={r.id}>
                <td>{new Date(r.created_at).toLocaleDateString("ko-KR")}</td>
                <td>
                  <span style={{
                    background: (r.request_type || r.type) === "exchange" ? "#C8813F"
                              : (r.request_type || r.type) === "cancel" ? "#f59e0b"
                              : "#ef4444",
                    color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600
                  }}>
                    {(r.request_type || r.type) === "exchange" ? "교환"
                      : (r.request_type || r.type) === "cancel" ? "취소"
                      : "반품"}
                  </span>
                </td>
                <td>{r.orders?.order_number || "-"}</td>
                <td>{r.orders?.customer_name || "-"}</td>
                <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.reason}</td>
                <td>
                  <Select
                    className="ao-status-select"
                    value={r.status}
                    style={{ background: STATUS_COLORS[r.status], color: "#fff", borderColor: STATUS_COLORS[r.status] }}
                    onChange={(e) => updateReturn(r.id, { status: e.target.value })}
                  >
                    {Object.entries(STATUS_MAP).map(([k, v]) => (
                      <option key={k} value={k} style={{ background: "#fff", color: STATUS_COLORS[k] }}>{v}</option>
                    ))}
                  </Select>
                </td>
                <td>
                  <button className="ao-detail-btn" onClick={() => setSelected(r)}>상세</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="ao-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <ReturnDetailModal
          selected={selected}
          STATUS_MAP={STATUS_MAP}
          STATUS_COLORS={STATUS_COLORS}
          onClose={() => { setSelected(null); setShowRejectInput(false); }}
          onSave={async (updates) => {
            await updateReturn(selected.id, updates);
            showAlert("저장 완료");
          }}
          onApprove={async (updates) => {
            await updateReturn(selected.id, updates);
            await approveRequest(selected);
          }}
          onReject={async (updates, rejectReason) => {
            await updateReturn(selected.id, updates);
            await rejectRequest({ ...selected, _rejectReason: rejectReason });
          }}
          showAlert={showAlert}
          showRejectInput={showRejectInput}
          setShowRejectInput={setShowRejectInput}
        />
      )}
    </div>
  );
}

function ReturnDetailModal({ selected, STATUS_MAP, STATUS_COLORS, onClose, onSave, onApprove, onReject, showAlert, showRejectInput, setShowRejectInput }) {
  const [form, setForm] = useState({
    shipping_cost_bearer: selected.shipping_cost_bearer || "customer",
    return_tracking_number: selected.return_tracking_number || "",
    new_tracking_number: selected.new_tracking_number || "",
    admin_memo: selected.admin_memo || "",
    reject_reason: selected.reject_reason || "",
  });

  function ef(key, val) { setForm((p) => ({ ...p, [key]: val })); }

  function getFormUpdates() {
    return {
      shipping_cost_bearer: form.shipping_cost_bearer,
      return_tracking_number: form.return_tracking_number,
      new_tracking_number: form.new_tracking_number,
      admin_memo: form.admin_memo,
    };
  }

  const rtype = selected.request_type || selected.type;
  const typeLabel = rtype === "cancel" ? "취소" : rtype === "exchange" ? "교환" : "반품";

  return (
    <div className="ao-modal-overlay" onClick={onClose}>
      <div className="ao-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{typeLabel} 상세</h3>
        <button className="ao-modal-close" onClick={onClose}>✕</button>

        <div className="ao-modal-body">
          <div className="ao-detail-sections">
            {/* 신청 정보 (읽기 전용) */}
            <div className="ao-detail-group">
              <h4>신청 정보</h4>
              <div className="ao-detail-grid">
                <div><strong>신청일</strong><span>{new Date(selected.created_at).toLocaleString("ko-KR")}</span></div>
                <div><strong>유형</strong><span>{typeLabel}</span></div>
                <div><strong>주문번호</strong><span>{selected.orders?.order_number || "-"}</span></div>
                <div><strong>고객명</strong><span>{selected.orders?.customer_name || "-"}</span></div>
                <div><strong>연락처</strong><span>{selected.orders?.customer_phone || "-"}</span></div>
                <div><strong>사유</strong><span>{selected.reason}</span></div>
                {selected.detail && <div><strong>상세</strong><span>{selected.detail}</span></div>}
              </div>
              {selected.photos && Array.isArray(selected.photos) && selected.photos.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                  {selected.photos.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6, border: "1px solid #E7DAC8" }} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* 처리 (편집 가능) */}
            <div className="ao-detail-group">
              <h4>처리</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* 취소: 추가 필드 없음 */}

                {/* 반품: 배송비 부담 + 반품 송장번호 */}
                {rtype === "return" && (
                  <>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      배송비 부담
                      <Select
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                        value={form.shipping_cost_bearer}
                        onChange={(e) => ef("shipping_cost_bearer", e.target.value)}
                      >
                        <option value="customer">고객 부담</option>
                        <option value="seller">판매자 부담</option>
                      </Select>
                    </label>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      반품 송장번호
                      <input
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                        value={form.return_tracking_number}
                        onChange={(e) => ef("return_tracking_number", e.target.value)}
                        placeholder="반품 송장번호"
                      />
                    </label>
                  </>
                )}

                {/* 교환: 배송비 부담 + 반품 송장번호 + 재발송 송장번호 */}
                {rtype === "exchange" && (
                  <>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      배송비 부담
                      <Select
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                        value={form.shipping_cost_bearer}
                        onChange={(e) => ef("shipping_cost_bearer", e.target.value)}
                      >
                        <option value="customer">고객 부담</option>
                        <option value="seller">판매자 부담</option>
                      </Select>
                    </label>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      반품 송장번호
                      <input
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                        value={form.return_tracking_number}
                        onChange={(e) => ef("return_tracking_number", e.target.value)}
                        placeholder="반품 송장번호"
                      />
                    </label>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      재발송 송장번호
                      <input
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                        value={form.new_tracking_number}
                        onChange={(e) => ef("new_tracking_number", e.target.value)}
                        placeholder="재발송 송장번호"
                      />
                    </label>
                  </>
                )}

                <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                  관리자 메모
                  <input
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #E7DAC8", borderRadius: 8, fontSize: 13, marginTop: 4 }}
                    value={form.admin_memo}
                    onChange={(e) => ef("admin_memo", e.target.value)}
                    placeholder="메모"
                  />
                </label>

                {/* 거절 사유 (거절 버튼 누르면 표시) */}
                {showRejectInput && (
                  <label style={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>
                    거절 사유 (필수, 고객에게 표시됨)
                    <textarea
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #ef4444", borderRadius: 8, fontSize: 13, marginTop: 4, minHeight: 60 }}
                      value={form.reject_reason}
                      onChange={(e) => ef("reject_reason", e.target.value)}
                      placeholder="거절 사유를 입력하세요"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* 상태 표시 */}
            {selected.status === "rejected" && selected.reject_reason && (
              <div style={{ padding: "12px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, color: "#991b1b" }}>
                <strong>거절 사유:</strong> {selected.reject_reason}
              </div>
            )}
            {selected.status === "completed" && (
              <div style={{ padding: "12px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13, color: "#166534", textAlign: "center", fontWeight: 700 }}>
                ✓ 처리 완료됨
              </div>
            )}
            {selected.status === "withdrawn" && (
              <div style={{ padding: "12px 14px", background: "#F4EADB", border: "1px solid #D9C7B0", borderRadius: 8, fontSize: 13, color: "#6b7280", textAlign: "center", fontWeight: 700 }}>
                고객이 신청을 철회했습니다
              </div>
            )}
          </div>

          {/* 버튼 영역 */}
          <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid #E7DAC8" }}>
            <button
              type="button"
              onClick={() => onSave(getFormUpdates())}
              style={{ flex: 1, padding: "12px 16px", background: "#fff", border: "1px solid #D9C7B0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              저장
            </button>
            {selected.status === "pending" && !showRejectInput && (
              <>
                <button
                  type="button"
                  onClick={() => onApprove(getFormUpdates())}
                  style={{ flex: 1, padding: "12px 16px", background: "#10b981", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                >
                  {rtype === "cancel" ? "✓ 승인 및 자동 환불" : "✓ 승인 (수거 진행)"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectInput(true)}
                  style={{ flex: 1, padding: "12px 16px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                >
                  ✕ 거절
                </button>
              </>
            )}
            {selected.status === "pending" && showRejectInput && (
              <>
                <button
                  type="button"
                  onClick={() => setShowRejectInput(false)}
                  style={{ flex: 1, padding: "12px 16px", background: "#fff", border: "1px solid #D9C7B0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!form.reject_reason.trim()) {
                      showAlert("거절 사유를 입력하세요.");
                      return;
                    }
                    onReject(getFormUpdates(), form.reject_reason);
                  }}
                  style={{ flex: 1, padding: "12px 16px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                >
                  거절 확인
                </button>
              </>
            )}
            {/* 반품: 수거 완료 후 환불 버튼 */}
            {/* 반품: 수거 확인 후 환불 */}
            {(rtype === "return" || rtype === "refund") && ["approved", "collecting", "collected"].includes(selected.status) && (
              <button
                type="button"
                onClick={async () => {
                  const updates = getFormUpdates();
                  await onSave(updates);
                  try {
                    const res = await fetch(`${API}/api/returns/${selected.id}/refund`, { method: "POST" });
                    const data = await res.json();
                    if (res.ok) {
                      showAlert("수거 확인 + 환불 처리 완료");
                      onClose();
                    } else {
                      showAlert(`환불 실패: ${data.error || JSON.stringify(data)}`);
                    }
                  } catch (e) {
                    showAlert("환불 요청 실패: " + e.message);
                  }
                }}
                style={{ flex: 1, padding: "12px 16px", background: "#7c4dff", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >
                💳 수거 확인 및 환불 처리
              </button>
            )}
            {/* 교환: 수거 확인 후 재발송 완료 */}
            {rtype === "exchange" && ["approved", "collecting", "collected"].includes(selected.status) && (
              <button
                type="button"
                onClick={async () => {
                  const updates = getFormUpdates();
                  await onSave(updates);
                  await onSave({ ...updates, status: "completed" });
                  showAlert("수거 확인 + 재발송 완료 처리됨");
                  onClose();
                }}
                style={{ flex: 1, padding: "12px 16px", background: "#C8813F", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >
                📦 수거 확인 및 재발송 완료
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
